import React, { useRef, useState, useEffect } from "react";
import { Play, Pause } from "lucide-react";
import Waveform from "./Waveform";

/**
 * AudioPostPlayer
 * iOS-Safari-reliable audio player. Uses an HTML5 <audio> element for
 * playback (Web Audio decode/playback was failing silently after recording
 * sessions, leaving the play button unresponsive). Web Audio is only used
 * once, offline, to extract a static waveform when the backend doesn't
 * already provide one — that path can fail without affecting playback.
 *
 * Props:
 * - audioUrl              URL of the audio file (or blob URL).
 * - waveformData          Pre-computed waveform from backend (1024 ints 0-255). Optional.
 * - showStaticWaveform    Show the static waveform (default true).
 * - onEnded               Callback when audio ends.
 * - onClick               Click handler for the container (e.g. to stopPropagation).
 * - loadImmediately       If true, fetch metadata immediately instead of waiting for viewport.
 */
const AudioPostPlayer = ({
	audioUrl,
	waveformData = null,
	showStaticWaveform = true,
	onEnded,
	onClick,
	loadImmediately = false,
}) => {
	const [isPlaying, setIsPlaying] = useState(false);
	const [duration, setDuration] = useState(0);
	const [currentTime, setCurrentTime] = useState(0);
	const [audioLevels, setAudioLevels] = useState(null);
	const [error, setError] = useState(null);
	const [isLoadingWaveform, setIsLoadingWaveform] = useState(false);
	const [isInViewport, setIsInViewport] = useState(loadImmediately);

	const audioElementRef = useRef(null);
	const realWaveformRef = useRef(null);
	const containerRef = useRef(null);
	const progressBarRef = useRef(null);
	const progressFillRef = useRef(null);
	const thumbRef = useRef(null);
	// Web Audio refs — used only for live waveform visualization while playing.
	// Audio still flows through the HTML5 element; we just tap it for analyser data.
	const playbackCtxRef = useRef(null);
	const mediaSourceRef = useRef(null);
	const analyserRef = useRef(null);
	const visualizationFrameRef = useRef(null);
	const isPlayingRef = useRef(false);
	// Capture callback / config props in refs so the audio-creation effect
	// doesn't tear down the <audio> element when a parent passes new inline
	// arrows for onEnded on every render.
	const onEndedRef = useRef(onEnded);
	const showStaticWaveformRef = useRef(showStaticWaveform);
	useEffect(() => {
		onEndedRef.current = onEnded;
	}, [onEnded]);
	useEffect(() => {
		showStaticWaveformRef.current = showStaticWaveform;
	}, [showStaticWaveform]);

	// ── Single IntersectionObserver: lazy-load on first appearance, and
	//     pause playback when scrolled out of view. Two thresholds let one
	//     observer drive both behaviors instead of installing two on the
	//     same element.
	useEffect(() => {
		if (!containerRef.current) {
			return undefined;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting && entry.intersectionRatio > 0) {
						setIsInViewport(true);
					}
					if (entry.intersectionRatio < 0.5 && isPlayingRef.current) {
						const audio = audioElementRef.current;
						if (audio) {
							audio.pause();
						}
						isPlayingRef.current = false;
						setIsPlaying(false);
						if (visualizationFrameRef.current) {
							cancelAnimationFrame(visualizationFrameRef.current);
							visualizationFrameRef.current = null;
						}
					}
				});
			},
			{ rootMargin: "50% 0px", threshold: [0, 0.5] }
		);
		observer.observe(containerRef.current);
		return () => observer.disconnect();
	}, []);

	// loadImmediately bypasses the lazy-load step entirely.
	useEffect(() => {
		if (loadImmediately) {
			setIsInViewport(true);
		}
	}, [loadImmediately]);

	// ── Use backend-provided waveform if present ──────────────────────────
	useEffect(() => {
		if (
			waveformData &&
			Array.isArray(waveformData) &&
			waveformData.length === 1024
		) {
			const validated = waveformData.map((v) => {
				const n = typeof v === "number" ? v : parseInt(v, 10);
				return Math.max(0, Math.min(255, Number.isNaN(n) ? 128 : n));
			});
			realWaveformRef.current = validated;
			setAudioLevels(validated);
			setIsLoadingWaveform(false);
		}
	}, [waveformData]);

	// ── Create the HTML5 <audio> element and wire its events ─────────────
	useEffect(() => {
		if (!audioUrl || !isInViewport) {
			return undefined;
		}

		const audio = new Audio();
		audio.preload = "metadata";
		audio.crossOrigin = "anonymous";
		audio.src = audioUrl;
		audioElementRef.current = audio;

		const onLoadedMetadata = () => {
			if (Number.isFinite(audio.duration) && audio.duration > 0) {
				setDuration(audio.duration);
			}
		};
		const onTimeUpdate = () => setCurrentTime(audio.currentTime);
		const onEndedHandler = () => {
			isPlayingRef.current = false;
			setIsPlaying(false);
			setCurrentTime(0);
			if (visualizationFrameRef.current) {
				cancelAnimationFrame(visualizationFrameRef.current);
				visualizationFrameRef.current = null;
			}
			if (showStaticWaveformRef.current && realWaveformRef.current) {
				setAudioLevels(realWaveformRef.current);
			}
			onEndedRef.current?.();
		};
		const onErrorHandler = () => {
			setError("Unable to load audio. Please try again.");
		};

		audio.addEventListener("loadedmetadata", onLoadedMetadata);
		audio.addEventListener("timeupdate", onTimeUpdate);
		audio.addEventListener("ended", onEndedHandler);
		audio.addEventListener("error", onErrorHandler);

		return () => {
			audio.removeEventListener("loadedmetadata", onLoadedMetadata);
			audio.removeEventListener("timeupdate", onTimeUpdate);
			audio.removeEventListener("ended", onEndedHandler);
			audio.removeEventListener("error", onErrorHandler);
			audio.pause();
			audio.src = "";
			audioElementRef.current = null;
			isPlayingRef.current = false;
			if (visualizationFrameRef.current) {
				cancelAnimationFrame(visualizationFrameRef.current);
				visualizationFrameRef.current = null;
			}
			// Disconnect Web Audio graph and close the context — iOS limits
			// concurrent contexts; leaking them eventually breaks new players.
			try {
				mediaSourceRef.current?.disconnect();
			} catch {
				/* ignore */
			}
			try {
				analyserRef.current?.disconnect();
			} catch {
				/* ignore */
			}
			playbackCtxRef.current?.close().catch(() => {});
			mediaSourceRef.current = null;
			analyserRef.current = null;
			playbackCtxRef.current = null;
		};
	}, [audioUrl, isInViewport]);

	// ── Extract static waveform via Web Audio (only if backend didn't provide) ──
	useEffect(() => {
		if (!audioUrl || !isInViewport) {
			return;
		}
		const hasBackendWaveform =
			waveformData &&
			Array.isArray(waveformData) &&
			waveformData.length === 1024;
		if (hasBackendWaveform) {
			return;
		}

		let cancelled = false;
		setIsLoadingWaveform(true);

		const extract = async () => {
			try {
				const response = await fetch(audioUrl, {
					mode: "cors",
					credentials: "omit",
				});
				if (!response.ok) {
					throw new Error(`Fetch failed: ${response.status}`);
				}
				const arrayBuffer = await response.arrayBuffer();
				if (cancelled) {
					return;
				}

				const Ctx = window.AudioContext || window.webkitAudioContext;
				if (!Ctx) {
					throw new Error("AudioContext unavailable");
				}
				const ctx = new Ctx();
				let audioBuffer;
				try {
					audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
				} finally {
					ctx.close().catch(() => {});
				}
				if (cancelled || !audioBuffer) {
					return;
				}

				const samples = 1024;
				const channelData = audioBuffer.getChannelData(0);
				const blockSize = Math.max(1, Math.floor(channelData.length / samples));
				const rms = new Array(samples);
				let globalMax = 0;
				for (let i = 0; i < samples; i++) {
					let sum = 0;
					const start = i * blockSize;
					const end = Math.min(start + blockSize, channelData.length);
					for (let j = start; j < end; j++) {
						sum += channelData[j] * channelData[j];
					}
					const v = Math.sqrt(sum / Math.max(1, end - start));
					rms[i] = v;
					if (v > globalMax) {
						globalMax = v;
					}
				}
				const norm = globalMax > 0 ? 127 / globalMax : 1;
				const waveform = rms.map((v) =>
					Math.max(0, Math.min(255, 128 + v * norm))
				);
				realWaveformRef.current = waveform;
				if (!cancelled) {
					setAudioLevels(waveform);
					setIsLoadingWaveform(false);
				}
			} catch (err) {
				console.warn("AudioPostPlayer: waveform extraction failed", err);
				if (!cancelled) {
					// Fall back to a flat default waveform — playback still works.
					const defaultWaveform = new Array(1024).fill(128);
					realWaveformRef.current = defaultWaveform;
					setAudioLevels(defaultWaveform);
					setIsLoadingWaveform(false);
				}
			}
		};

		extract();
		return () => {
			cancelled = true;
		};
	}, [audioUrl, isInViewport, waveformData]);

	// ── Visualization loop ────────────────────────────────────────────────
	const stopVisualization = () => {
		if (visualizationFrameRef.current) {
			cancelAnimationFrame(visualizationFrameRef.current);
			visualizationFrameRef.current = null;
		}
		if (showStaticWaveformRef.current && realWaveformRef.current) {
			setAudioLevels(realWaveformRef.current);
		}
	};
	const startVisualization = () => {
		const analyser = analyserRef.current;
		if (!analyser) {
			return;
		}
		const buffer = new Uint8Array(analyser.frequencyBinCount);
		const tick = () => {
			if (!isPlayingRef.current || !analyserRef.current) {
				stopVisualization();
				return;
			}
			analyserRef.current.getByteTimeDomainData(buffer);
			// Safari returns all 128s if the graph isn't producing audio yet — keep
			// showing the static waveform until real data arrives.
			if (buffer.some((v) => v !== 128)) {
				setAudioLevels(Array.from(buffer));
			}
			visualizationFrameRef.current = requestAnimationFrame(tick);
		};
		visualizationFrameRef.current = requestAnimationFrame(tick);
	};

	// ── Wire HTML5 audio through Web Audio analyser (one-time, on first play) ──
	const ensureAnalyserGraph = async () => {
		const audio = audioElementRef.current;
		if (!audio) {
			return false;
		}
		if (analyserRef.current && playbackCtxRef.current) {
			// Graph exists — just make sure context is running.
			if (playbackCtxRef.current.state === "suspended") {
				try {
					await playbackCtxRef.current.resume();
				} catch (err) {
					console.warn("AudioPostPlayer: ctx.resume() failed", err);
				}
			}
			return true;
		}
		try {
			const Ctx = window.AudioContext || window.webkitAudioContext;
			if (!Ctx) {
				return false;
			}
			const ctx = new Ctx();
			if (ctx.state === "suspended") {
				await ctx.resume();
			}
			// createMediaElementSource can only be called once per element.
			const source = ctx.createMediaElementSource(audio);
			const analyser = ctx.createAnalyser();
			analyser.fftSize = 2048;
			analyser.smoothingTimeConstant = 0.8;
			source.connect(analyser);
			analyser.connect(ctx.destination);
			playbackCtxRef.current = ctx;
			mediaSourceRef.current = source;
			analyserRef.current = analyser;
			return true;
		} catch (err) {
			console.warn(
				"AudioPostPlayer: analyser graph setup failed; playing without live visualization",
				err
			);
			return false;
		}
	};

	// ── Play / pause ──────────────────────────────────────────────────────
	const togglePlay = async (e) => {
		if (e) {
			e.stopPropagation();
			e.preventDefault();
		}
		const audio = audioElementRef.current;
		if (!audio) {
			return;
		}
		if (isPlaying) {
			audio.pause();
			isPlayingRef.current = false;
			setIsPlaying(false);
			stopVisualization();
			return;
		}
		// Wire analyser before play so the first frame has real data.
		// Failure is non-fatal — audio still plays via HTML5 element.
		await ensureAnalyserGraph();
		try {
			await audio.play();
			isPlayingRef.current = true;
			setIsPlaying(true);
			setError(null);
			startVisualization();
		} catch (err) {
			console.error("AudioPostPlayer: play() rejected", err);
			setError("Tap play again to start audio.");
		}
	};

	// ── Seek ──────────────────────────────────────────────────────────────
	const handleSeek = (seekEvent) => {
		seekEvent.stopPropagation?.();
		const audio = audioElementRef.current;
		if (!audio || !progressBarRef.current || !duration) {
			return;
		}
		const rect = progressBarRef.current.getBoundingClientRect();
		const x = Math.max(0, Math.min(seekEvent.clientX - rect.left, rect.width));
		const percentage = x / rect.width;
		const newTime = percentage * duration;
		if (Number.isFinite(newTime)) {
			audio.currentTime = newTime;
			setCurrentTime(newTime);
		}
	};

	// ── Format time helper ────────────────────────────────────────────────
	const formatTime = (seconds) => {
		if (
			typeof seconds !== "number" ||
			Number.isNaN(seconds) ||
			!Number.isFinite(seconds) ||
			seconds < 0
		) {
			return "00:00";
		}
		const mins = Math.floor(seconds / 60);
		const secs = Math.floor(seconds % 60);
		return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
	};

	return (
		<div ref={containerRef} className="w-full" onClick={onClick}>
			<div
				className="w-full space-y-4 p-4"
				style={{
					backgroundColor: "rgba(0, 0, 0, 0.9)",
					borderColor: "rgba(234, 179, 8, 0.3)",
				}}
			>
				{error ? (
					<div className="mb-2 rounded bg-red-500/20 p-2 text-center text-sm text-red-400">
						{error}
					</div>
				) : null}

				{isLoadingWaveform && audioLevels === null ? (
					<div
						className="flex min-h-[200px] items-center justify-center rounded-2xl border-2 p-4"
						style={{
							backgroundColor: "rgba(0, 0, 0, 0.9)",
							borderColor: "rgba(234, 179, 8, 0.3)",
						}}
					>
						<div className="text-center">
							<div
								className="mx-auto mb-3 h-12 w-12 animate-spin rounded-full border-4 border-gray-700"
								style={{ borderTopColor: "#fbbf24" }}
							/>
							<p className="text-sm text-gray-400">Loading waveform...</p>
						</div>
					</div>
				) : (
					<Waveform audioLevels={audioLevels} isRecording={false} />
				)}

				<div className="space-y-3">
					<div className="flex items-center gap-4">
						<button
							onClick={togglePlay}
							className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border-2 shadow-lg transition-all"
							style={{
								borderColor: "#fbbf24",
								backgroundColor: "#fbbf24",
								boxShadow: isPlaying
									? "0 0 20px rgba(251, 191, 36, 0.6)"
									: "0 0 10px rgba(251, 191, 36, 0.3)",
							}}
							aria-label={isPlaying ? "Pause audio" : "Play audio"}
						>
							{isPlaying ? (
								<Pause size={20} fill="white" color="white" />
							) : (
								<Play size={20} fill="white" color="white" />
							)}
						</button>

						<div className="flex-1">
							<div
								ref={progressBarRef}
								className="group relative h-2 cursor-pointer rounded-full bg-gray-700"
								onClick={handleSeek}
							>
								<div
									ref={progressFillRef}
									className="absolute left-0 top-0 h-full rounded-full"
									style={{
										width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
										backgroundColor: "#fbbf24",
										boxShadow: "0 0 10px rgba(251, 191, 36, 0.6)",
									}}
								/>
								<div
									ref={thumbRef}
									className="absolute h-4 w-4 cursor-grab rounded-full border-2 border-white shadow-lg active:cursor-grabbing group-hover:scale-110"
									style={{
										left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
										top: "50%",
										backgroundColor: "#fbbf24",
										boxShadow: "0 0 10px rgba(251, 191, 36, 0.8)",
										transform: "translate(-50%, -50%)",
										pointerEvents: "auto",
									}}
									onMouseDown={(downEvent) => {
										downEvent.stopPropagation();
										downEvent.preventDefault();
										const onMove = (moveEvent) => {
											const bar = progressBarRef.current;
											if (!bar) {
												return;
											}
											const rect = bar.getBoundingClientRect();
											const x = Math.max(
												0,
												Math.min(moveEvent.clientX - rect.left, rect.width)
											);
											const pct = x / rect.width;
											if (progressFillRef.current) {
												progressFillRef.current.style.width = `${pct * 100}%`;
											}
											if (thumbRef.current) {
												thumbRef.current.style.left = `${pct * 100}%`;
											}
											setCurrentTime(pct * duration);
										};
										const onUp = (upEvent) => {
											document.removeEventListener("mousemove", onMove);
											document.removeEventListener("mouseup", onUp);
											handleSeek({
												stopPropagation: () => {},
												clientX: upEvent.clientX,
											});
										};
										document.addEventListener("mousemove", onMove);
										document.addEventListener("mouseup", onUp);
									}}
									onTouchStart={(touchStartEvent) => {
										touchStartEvent.stopPropagation();
										const onMove = (moveEvent) => {
											moveEvent.preventDefault();
											const bar = progressBarRef.current;
											const [touch] = moveEvent.touches;
											if (!bar || !touch) {
												return;
											}
											const rect = bar.getBoundingClientRect();
											const x = Math.max(
												0,
												Math.min(touch.clientX - rect.left, rect.width)
											);
											const pct = x / rect.width;
											if (progressFillRef.current) {
												progressFillRef.current.style.width = `${pct * 100}%`;
											}
											if (thumbRef.current) {
												thumbRef.current.style.left = `${pct * 100}%`;
											}
											setCurrentTime(pct * duration);
										};
										const onEndTouch = (endEvent) => {
											document.removeEventListener("touchmove", onMove);
											document.removeEventListener("touchend", onEndTouch);
											const touch =
												endEvent.changedTouches?.[0] || endEvent.touches?.[0];
											if (touch) {
												handleSeek({
													stopPropagation: () => {},
													clientX: touch.clientX,
												});
											}
										};
										document.addEventListener("touchmove", onMove, {
											passive: false,
										});
										document.addEventListener("touchend", onEndTouch);
									}}
								/>
							</div>
						</div>
					</div>

					<div
						className="flex w-full justify-between px-2 font-mono text-xs font-semibold"
						style={{ color: "#fbbf24" }}
					>
						<div>{formatTime(currentTime)}</div>
						<div>{formatTime(duration)}</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AudioPostPlayer;
